import { useEffect, useRef, useState } from 'react';
import { IoChevronDown, IoClose, IoMenu } from 'react-icons/io5';
import { getAuthUrl, PRODUCT_URLS } from '@podus/utils';
import ServicesMegaMenu from './ServicesMegaMenu';
import podusLogoFull from './assets/podus-logo-full.svg';
import podusAiLogoFull from './assets/podus-ai-logo-full.svg';
import podusLogoSmall from './assets/podus-logo-small.png';
import multidoorLogoFull from './assets/podus-multidoor-full.svg';

const PRODUCT_LOGOS: Record<string, string> = {
    'podus-ai': podusAiLogoFull,
    'podus-multidoor': multidoorLogoFull,
};

/* ─── Types ─── */

export interface NavbarLabels {
    home?: string;
    services?: string;
    help?: string;
    signUp?: string;
    login?: string;
    signIn?: string;
    getStarted?: string;
}

export interface NavbarLanguage {
    code: string;
    native: string;
}

export interface LandingPagesNavbarProps {
    /** Product name — changes the navbar logo. Defaults to the generic Podus logo. */
    product?: string;
    /** Override default English labels */
    labels?: NavbarLabels;
    /** Language selector config. Omit to hide the language selector. */
    language?: {
        current: string;
        languages: NavbarLanguage[];
        onChange: (code: string) => void;
    };
    /** Custom services mega menu to replace the default ServicesNavButton dropdown.
     *  Receives open state and a ref to attach to the dropdown container. */
    renderServicesMegaMenu?: (props: {
        open: boolean;
        dropdownRef: React.RefObject<HTMLDivElement | null>;
    }) => React.ReactNode;
    /** Home link URL (default: "/") */
    homeHref?: string;
    /** Help link URL (default: "/help") */
    helpHref?: string;
}

/* ─── Defaults ─── */

const DEFAULT_LABELS: Required<NavbarLabels> = {
    home: 'Home',
    services: 'Services',
    help: 'Help',
    signUp: 'Sign Up',
    login: 'Login',
    signIn: 'Sign In',
    getStarted: 'Get Started',
};

/* ─── Component ─── */

export default function LandingPagesNavbar({
    product,
    labels: labelsProp,
    language,
    renderServicesMegaMenu,
    homeHref = '/',
    helpHref = PRODUCT_URLS.podushelp,
}: LandingPagesNavbarProps) {
    const labels = { ...DEFAULT_LABELS, ...labelsProp };
    const authBase = getAuthUrl();
    const logo = (product && PRODUCT_LOGOS[product]) || podusLogoFull;

    const [menuVisible, setMenuVisible] = useState(false);
    const [menuClosing, setMenuClosing] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const langRef = useRef<HTMLDivElement>(null);
    const langBtnRef = useRef<HTMLButtonElement>(null);

    const openMenu = () => {
        setMenuVisible(true);
        setMenuClosing(false);
    };

    const closeMenu = () => {
        setMenuClosing(true);
        // Fallback: force close if onAnimationEnd doesn't fire
        setTimeout(() => {
            setMenuVisible(false);
            setMenuClosing(false);
        }, 400);
    };

    const handleAnimationEnd = () => {
        if (menuClosing) {
            setMenuVisible(false);
            setMenuClosing(false);
        }
    };

    // Close services dropdown on outside click
    useEffect(() => {
        if (!servicesOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(e.target as Node)
            ) {
                setServicesOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [servicesOpen, renderServicesMegaMenu]);

    // Close language dropdown on outside click
    useEffect(() => {
        if (!langOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                langRef.current &&
                !langRef.current.contains(e.target as Node) &&
                langBtnRef.current &&
                !langBtnRef.current.contains(e.target as Node)
            ) {
                setLangOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [langOpen]);

    const navRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // Find the nearest scrollable ancestor (for custom scroll containers like page-scroll-root)
        let scrollTarget: HTMLElement | Window = window;
        let parent = navRef.current?.parentElement ?? null;
        while (parent) {
            const { overflowY } = getComputedStyle(parent);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollTarget = parent;
                break;
            }
            parent = parent.parentElement;
        }
        const onScroll = () => {
            const scrollTop = scrollTarget instanceof Window ? window.scrollY : scrollTarget.scrollTop;
            setScrolled(scrollTop > 0);
        };
        onScroll();
        scrollTarget.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollTarget.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <div
            ref={navRef}
            className={`sticky left-0 right-0 top-0 z-2000 mx-auto w-full bg-white/70 backdrop-blur-xl transition-[border-color] duration-200 ${scrolled ? 'border-b border-[#ddd]' : 'border-b border-transparent'}`}
        >
            <header className="max-w-[1920px] m-auto">
                {/* Desktop Navbar */}
                <nav className="hidden h-[76px] grid-cols-3 items-center px-16 lg:grid xl:px-20">
                    {/* Left Nav Links */}
                    <div className="flex items-center gap-8">
                        <a
                            href={homeHref}
                            className="text-secondary hover:text-primary text-[15px] font-medium transition-colors"
                        >
                            {labels.home}
                        </a>
                        <button
                            ref={triggerRef}
                            onClick={() => setServicesOpen((p) => !p)}
                            className={`flex items-center gap-1.5 text-[15px] font-medium transition-colors ${
                                servicesOpen ? 'text-primary' : 'text-secondary hover:text-primary'
                            }`}
                        >
                            {labels.services}
                            <IoChevronDown
                                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                    servicesOpen ? 'rotate-180' : ''
                                }`}
                            />
                        </button>
                    </div>

                    {/* Center Logo */}
                    <div className="flex justify-center">
                        <a href={homeHref}>
                            <img src={logo} alt="Podus" className="h-6" />
                        </a>
                    </div>

                    {/* Right Actions */}
                    <div className="flex items-center justify-end gap-7">
                        <a
                            href={helpHref + '/contact'}
                            className="text-secondary hover:text-primary text-[15px] font-medium transition-colors"
                        >
                            {labels.help}
                        </a>

                        {/* Language Selector — Desktop */}
                        {language && (
                            <div className="relative">
                                <button
                                    ref={langBtnRef}
                                    onClick={() => setLangOpen((p) => !p)}
                                    className={`flex items-center gap-1.5 text-[15px] font-medium transition-colors ${
                                        langOpen ? 'text-primary' : 'text-secondary hover:text-primary'
                                    }`}
                                >
                                    {language.current.toUpperCase()}
                                    <IoChevronDown
                                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                            langOpen ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>

                                {langOpen && (
                                    <div
                                        ref={langRef}
                                        className="absolute right-0 top-full mt-2 w-44 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg"
                                    >
                                        {language.languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onClick={() => {
                                                    language.onChange(lang.code);
                                                    setLangOpen(false);
                                                }}
                                                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                                                    language.current === lang.code
                                                        ? 'bg-gray-50 font-semibold text-primary'
                                                        : 'text-secondary hover:bg-gray-50'
                                                }`}
                                            >
                                                <span className="min-w-[24px] font-medium">
                                                    {lang.code.toUpperCase()}
                                                </span>
                                                <span>{lang.native}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <a
                            href={`${authBase}/sign-up`}
                            className="text-secondary hover:text-primary text-[15px] font-medium transition-colors"
                        >
                            {labels.signUp}
                        </a>
                        <a
                            href={`${authBase}/sign-in`}
                            className="bg-secondary hover:bg-secondary-400 rounded-full px-7 py-2.5 text-[15px] font-medium text-white transition-colors"
                        >
                            {labels.login}
                        </a>
                    </div>
                </nav>

                {/* ─── Services Mega Menu (Desktop) ─── */}
                {renderServicesMegaMenu ? (
                    renderServicesMegaMenu({ open: servicesOpen, dropdownRef })
                ) : (
                    <ServicesMegaMenu open={servicesOpen} dropdownRef={dropdownRef} />
                )}

                {/* Mobile Navbar */}
                <nav className="flex h-[68px] items-center justify-between px-5 lg:hidden">
                    <a href={homeHref}>
                        <img src={logo} alt="Podus" className="h-5" />
                    </a>
                    <button
                        onClick={menuVisible ? closeMenu : openMenu}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 transition-colors hover:bg-gray-50"
                        aria-label={menuVisible ? 'Close menu' : 'Open menu'}
                    >
                        {menuVisible ? (
                            <IoClose className="text-secondary h-5 w-5" />
                        ) : (
                            <IoMenu className="text-secondary h-5 w-5" />
                        )}
                    </button>
                </nav>

                {/* Mobile Menu — Full screen slide-in/out from right */}
                {menuVisible && (
                    <div
                        className={`fixed inset-0 z-2001 flex flex-col bg-white lg:hidden h-dvh ${
                            menuClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'
                        }`}
                        onAnimationEnd={handleAnimationEnd}
                    >
                        {/* Menu Header */}
                        <div className="flex h-[68px] items-center justify-between border-b border-gray-100 px-5">
                            <a href={homeHref}>
                                <img src={logo} alt="Podus" className="h-5" />
                            </a>
                            <button onClick={closeMenu} aria-label="Close menu">
                                <IoClose className="text-secondary h-6 w-6" />
                            </button>
                        </div>

                        {/* Menu Content */}
                        <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-6 pt-8">
                            {/* Primary Links */}
                            <div className="flex flex-col gap-7">
                                <a href={homeHref} className="text-secondary text-lg font-semibold">
                                    {labels.home}
                                </a>
                                <a href={helpHref + '/contact'} className="text-secondary text-lg font-semibold">
                                    {labels.help}
                                </a>
                            </div>

                            {/* Language Selector — Mobile */}
                            {language && (
                                <div className="mt-8">
                                    <span className="text-secondary mb-3 block text-sm font-semibold uppercase tracking-wider">
                                        {language.languages.find((l) => l.code === language.current)?.native}
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {language.languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onClick={() => language.onChange(lang.code)}
                                                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                                    language.current === lang.code
                                                        ? 'bg-secondary text-white'
                                                        : 'bg-gray-100 text-secondary hover:bg-gray-200'
                                                }`}
                                            >
                                                {lang.native}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Services Section */}
                            <div className="mt-8 border-t border-gray-100 pt-8">
                                <h3 className="text-secondary mb-4 text-lg font-semibold">{labels.services}</h3>
                                <div className="flex flex-col gap-4">
                                    <a href={PRODUCT_URLS.chatpodusai} className="text-secondary-100 text-base">
                                        Podus AI
                                    </a>
                                    <a href={PRODUCT_URLS.poduscontract} className="text-secondary-100 text-base">
                                        Podus Contract
                                    </a>
                                    <a href={PRODUCT_URLS.podusappointment} className="text-secondary-100 text-base">
                                        Podus Appointment
                                    </a>
                                    <a href={PRODUCT_URLS.podusaid} className="text-secondary-100 text-base">
                                        Podus Legal Aid
                                    </a>
                                    <a href={PRODUCT_URLS.poduschecker} className="text-secondary-100 text-base">
                                        Podus Checker
                                    </a>
                                </div>
                            </div>

                            {/* Spacer */}
                            <div className="flex-1" />

                            {/* Bottom Buttons */}
                            <div className="mt-8 flex items-center gap-3 border-t border-gray-100 pt-6">
                                <a
                                    href={`${authBase}/sign-in`}
                                    className="text-secondary flex-1 rounded-full border border-gray-300 py-3.5 text-center text-base font-medium transition-colors hover:bg-gray-50"
                                >
                                    {labels.signIn}
                                </a>
                                <a
                                    href={`${authBase}/sign-up`}
                                    className="bg-secondary hover:bg-secondary-400 flex-1 rounded-full py-3.5 text-center text-base font-medium text-white transition-colors"
                                >
                                    {labels.getStarted}
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </header>
        </div>
    );
}
